import {
  BranchMove,
  childrenOf,
  flattenMoveTree,
  mainlineChild,
} from './moveTreeBranchOps';

/**
 * Pure operations over the chapter move tree.
 *
 * Tree shape (mirrors the backend's serializer):
 *  - The top-level array IS the mainline: moves[i + 1] continues moves[i].
 *  - node.child_moves are the *alternative* next moves after node — except
 *    inside a variation, where child_moves[0] is that variation's own
 *    continuation and child_moves[1..] are sub-variations (see
 *    WorkbookMovesList's buildBranches, and _build_study_move_tree in
 *    chess/app/study_utils.py).
 *  - priority 1 marks a node as the mainline child *of its own parent* -
 *    a purely local fact (like lichess's children[0]), not a chapter-wide
 *    flag. Where that isn't unique among a set of siblings (legacy data,
 *    e.g. a PGN-imported variation whose descendants are all flatly
 *    priority=2), the smallest id wins instead - see mainlineChild in
 *    moveTreeBranchOps.ts.
 *
 * Every function returns a new tree (structural sharing where unchanged);
 * none of them mutate their input.
 */

export type AddMoveResult = {
  tree: BranchMove[];
  /** The node to select after the operation (never null unless blocked). */
  node: BranchMove | null;
  /**
   * - extend-main: appended to the top-level mainline; save to backend
   * - new-branch: inserted as a variation; save to backend
   * - navigate: the move already existed — select it, nothing to save
   * - blocked: rejected (different first move from a fixed start position)
   */
  kind: 'extend-main' | 'new-branch' | 'navigate' | 'blocked';
};

/** Piece placement + side to move — enough to identify a position for tree
 * matching without dragging in move counters (which differ between a
 * mainline node and the same position reached inside a variation).
 * Castling/en-passant rights are deliberately ignored too, for the same
 * reason: two paths to the same placement can disagree on them. */
export function isSamePosition(
  fen1: string | undefined | null,
  fen2: string | undefined | null,
): boolean {
  if (!fen1 || !fen2) {
    return false;
  }
  const [placement1, turn1] = fen1.split(' ');
  const [placement2, turn2] = fen2.split(' ');
  return placement1 === placement2 && turn1 === turn2;
}

/** Recursively applies a partial update to one node anywhere in the tree. */
export function patchMoveInTree(
  tree: BranchMove[],
  moveId: string | number,
  patch: Record<string, any>,
): BranchMove[] {
  let changed = false;
  const next = tree.map(node => {
    if (node.id === moveId) {
      changed = true;
      return {...node, ...patch};
    }
    if (node.child_moves?.length) {
      const children = patchMoveInTree(node.child_moves, moveId, patch);
      if (children !== node.child_moves) {
        changed = true;
        return {...node, child_moves: children};
      }
    }
    return node;
  });
  return changed ? next : tree;
}

/** Swaps a temp id for the persisted one — on the node itself and on any
 * node that recorded the temp id as its `parent`. */
export function replaceNodeId(
  tree: BranchMove[],
  tempId: string | number,
  realId: string | number,
): BranchMove[] {
  return tree.map(node => {
    const idHit = node.id === tempId;
    const parentHit = node.parent === tempId;
    const children = node.child_moves?.length
      ? replaceNodeId(node.child_moves, tempId, realId)
      : node.child_moves;
    if (!idHit && !parentHit && children === node.child_moves) {
      return node;
    }
    return {
      ...node,
      id: idHit ? realId : node.id,
      parent: parentHit ? realId : node.parent,
      child_moves: children,
    };
  });
}

/** Removes a move and everything after/under it: a mainline hit truncates
 * the top-level array from that index; a variation hit is spliced out of
 * whichever child_moves array holds it. Mirrors the backend's cascade. */
export function deleteMoveLocally(
  tree: BranchMove[] | undefined | null,
  moveId: string | number | null | undefined,
): BranchMove[] {
  if (!tree?.length || moveId == null) {
    return tree || [];
  }

  const idx = tree.findIndex(m => m?.id === moveId);
  if (idx !== -1) {
    return tree.slice(0, idx);
  }

  const pruneFromChildren = (children: BranchMove[]): BranchMove[] => {
    const childIdx = children.findIndex(c => c?.id === moveId);
    if (childIdx !== -1) {
      return [...children.slice(0, childIdx), ...children.slice(childIdx + 1)];
    }
    let changed = false;
    const next = children.map(c => {
      if (!c?.child_moves?.length) {
        return c;
      }
      const pruned = pruneFromChildren(c.child_moves);
      if (pruned === c.child_moves) {
        return c;
      }
      changed = true;
      return {...c, child_moves: pruned};
    });
    return changed ? next : children;
  };

  let changed = false;
  const next = tree.map(m => {
    if (!m?.child_moves?.length) {
      return m;
    }
    const pruned = pruneFromChildren(m.child_moves);
    if (pruned === m.child_moves) {
      return m;
    }
    changed = true;
    return {...m, child_moves: pruned};
  });
  return changed ? next : tree;
}

/**
 * Registers a played move against the tree, returning what happened:
 * extend the mainline, navigate to an existing node (mainline continuation
 * or an existing variation child), insert a new variation, or blocked
 * (a different first move from a fixed start position).
 */
export function addMoveToTree(
  tree: BranchMove[],
  args: {
    lastFen: string;
    newFen: string;
    san: string;
    tempId: string;
  },
): AddMoveResult {
  const {lastFen, newFen, san, tempId} = args;

  const makeNode = (
    parentId: string | number | null,
    isMain: boolean,
    priority: number,
  ): BranchMove => ({
    id: tempId,
    fen: newFen,
    lastFen,
    san,
    move: san,
    newFen,
    parent: parentId,
    isMainBranch: isMain,
    priority,
    child_moves: [],
  });

  // Extending the mainline: the played-from position is the current tip.
  if (tree.length > 0 && isSamePosition(tree[tree.length - 1].fen, lastFen)) {
    const tip = tree[tree.length - 1];
    // Already-played continuation among the tip's variations?
    const existingChild = (tip.child_moves || []).find(c =>
      isSamePosition(c.fen, newFen),
    );
    if (existingChild) {
      return {tree, node: existingChild, kind: 'navigate'};
    }
    const node = makeNode(tip.id ?? null, true, 1);
    return {tree: [...tree, node], node, kind: 'extend-main'};
  }

  // First move of an empty chapter.
  if (tree.length === 0) {
    const node = makeNode(null, true, 1);
    return {tree: [node], node, kind: 'extend-main'};
  }

  // Somewhere inside the tree: find the node whose position was played from.
  type Hit =
    | {where: 'main'; index: number}
    | {where: 'nested'; node: BranchMove};

  const findHit = (): Hit | null => {
    for (let i = 0; i < tree.length; i++) {
      if (isSamePosition(tree[i].fen, lastFen)) {
        return {where: 'main', index: i};
      }
    }
    let found: BranchMove | null = null;
    const walk = (nodes: BranchMove[] | undefined) => {
      if (!nodes?.length || found) {
        return;
      }
      for (const n of nodes) {
        if (found) {
          return;
        }
        if (isSamePosition(n.fen, lastFen)) {
          found = n;
          return;
        }
        walk(n.child_moves);
      }
    };
    for (const m of tree) {
      walk(m.child_moves);
      if (found) {
        break;
      }
    }
    return found ? {where: 'nested', node: found} : null;
  };

  const hit = findHit();

  if (hit) {
    const parentNode = hit.where === 'main' ? tree[hit.index] : hit.node;

    // Existing continuation? (mainline: the next flat entry; either kind:
    // any child_moves entry.)
    if (hit.where === 'main' && hit.index + 1 < tree.length) {
      const nextMain = tree[hit.index + 1];
      if (isSamePosition(nextMain.fen, newFen)) {
        return {tree, node: nextMain, kind: 'navigate'};
      }
    }
    const existingChild = (parentNode.child_moves || []).find(c =>
      isSamePosition(c.fen, newFen),
    );
    if (existingChild) {
      return {tree, node: existingChild, kind: 'navigate'};
    }

    // New variation under parentNode. Inside a variation an empty
    // child_moves means this is the line's continuation (slot 0); either
    // way sibling priorities start at 2 (1 is reserved for the mainline —
    // see getDeleteImpactCount's doc comment in moveTreeBranchOps).
    const priority = (parentNode.child_moves?.length || 0) + 2;
    const node = makeNode(parentNode.id ?? null, false, priority);
    const nextTree = patchMoveInTree(tree, parentNode.id, {
      child_moves: [...(parentNode.child_moves || []), node],
    });
    return {tree: nextTree, node, kind: 'new-branch'};
  }

  // Played from the fixed start position (before move 1).
  const first = tree[0];
  if (first && isSamePosition(first.fen, newFen)) {
    return {tree, node: first, kind: 'navigate'};
  }
  if (first) {
    // A different first move would fork the chapter's start — not supported.
    return {tree, node: null, kind: 'blocked'};
  }

  const node = makeNode(null, true, 1);
  return {tree: [...tree, node], node, kind: 'extend-main'};
}

/**
 * Rebuilds the tree the same way the backend's _build_study_move_tree does:
 * walk from the chapter root following `mainlineChild` at every level to
 * produce the flat top-level array, and recursively nest each node's other
 * children (mainline-eligible child first, if any, then the rest by id)
 * under child_moves. Used after promoteBranchLocally edits priorities on a
 * flat node map, so the resulting tree shape always matches what the server
 * would compute from the same rows.
 */
function buildTreeFromFlat(flatById: Map<string, BranchMove>): BranchMove[] {
  const buildNode = (
    node: BranchMove,
    skipChildId?: string | number | null,
  ): BranchMove => {
    const children = childrenOf(flatById, node.id).filter(
      c => c.id !== skipChildId,
    );
    const mainline = mainlineChild(children);
    const ordered = mainline
      ? [mainline, ...children.filter(c => c !== mainline)]
      : children;
    return {...node, child_moves: ordered.map(c => buildNode(c))};
  };

  const mainlineRows: BranchMove[] = [];
  let current = mainlineChild(childrenOf(flatById, null));
  while (current) {
    mainlineRows.push(current);
    current = mainlineChild(childrenOf(flatById, current.id));
  }

  return mainlineRows.map((row, i) => {
    const next = mainlineRows[i + 1];
    return buildNode(row, next ? next.id : null);
  });
}

/**
 * Optimistic local mirror of the backend's promote_branch: walks from
 * `moveId` up to the chapter root, and at every ancestor level swaps in
 * that ancestor as the mainline child of its parent if it isn't already —
 * a purely local, per-level operation (mirrors lila's tree.ts `promoteAt`).
 * Nothing below the ancestor path is ever touched: a sibling's subtree, or
 * the clicked move's own children, keep whatever priority/nesting they
 * already had. The tree is then rebuilt from the flat, priority-patched
 * node set exactly like the server's _build_study_move_tree, so this is a
 * true (if local) mirror rather than an approximation. Returns the tree
 * unchanged when there's nothing to do (unknown id, or moveId is already
 * mainline at every level).
 */
export function promoteBranchLocally(
  tree: BranchMove[],
  moveId: string | number,
): BranchMove[] {
  if (!tree?.length) {
    return tree || [];
  }

  const flat = flattenMoveTree(tree);
  if (!flat.has(String(moveId))) {
    return tree;
  }

  let changed = false;
  let node: BranchMove | undefined = flat.get(String(moveId));
  while (node) {
    const parentId = node.parent ?? null;
    const siblings = childrenOf(flat, parentId);
    const current = mainlineChild(siblings);
    if (!current || String(current.id) !== String(node.id)) {
      changed = true;
      if (current && (current.priority ?? 1) === 1) {
        flat.set(String(current.id), {...current, priority: 2});
      }
      if ((node.priority ?? 1) !== 1) {
        const promoted = {...node, priority: 1};
        flat.set(String(node.id), promoted);
        node = promoted;
      }
    }
    if (parentId == null) {
      break;
    }
    node = flat.get(String(parentId));
  }

  if (!changed) {
    return tree;
  }
  return buildTreeFromFlat(flat);
}
